import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardHeader from '../../components/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import '../../App.css';
import client from '../../api/client';
import { FaEnvelope, FaPhone, FaLock, FaUser, FaEye, FaEyeSlash } from 'react-icons/fa';

function AdminUserRegister() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    emailID: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    role: 'Admin',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
    } else if (formData.fullName.length > 100) {
      newErrors.fullName = 'Full Name must not exceed 100 characters';
    }

    // Email validation
    if (!formData.emailID.trim()) {
      newErrors.emailID = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailID)) {
      newErrors.emailID = 'Please enter a valid email address';
    } else if (formData.emailID.length > 100) {
      newErrors.emailID = 'Email must not exceed 100 characters';
    }

    // Mobile number validation
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber.replace(/[^\d]/g, ''))) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    } else if (formData.mobileNumber.length > 15) {
      newErrors.mobileNumber = 'Mobile number must not exceed 15 characters';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (formData.password.length > 255) {
      newErrors.password = 'Password must not exceed 255 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }
    

    setIsLoading(true);

    try {
      const payload = {
        fullName: formData.fullName.trim(),
        emailID: formData.emailID.trim(),
        mobileNumber: formData.mobileNumber.trim(),
        password: formData.password,
        role: formData.role,
        isActive: formData.isActive
      };

      const response = await client.post('/api/users', payload);

      if (response.status === 200 || response.status === 201) {
        toast.success('Admin user registered successfully!');
        // Reset form
        setFormData({
          fullName: '',
          emailID: '',
          mobileNumber: '',
          password: '',
          confirmPassword: '',
          role: 'Admin',
          isActive: true
        });
        // Navigate to login page
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to register admin user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <DashboardHeader userType="admin" userName={user?.displayName || 'Admin'} />

      <main className="dashboard-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="card shadow">
                <div className="card-body p-4">
                  <h2 className="card-title text-center mb-4">Register Admin User</h2>

                  <form onSubmit={handleSubmit}>
                    {/* Full Name Field */}
                    <div className="mb-4">
                      <label htmlFor="fullName" className="form-label fw-bold">
                         Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaUser />
                        </span>
                        <input
                          type="text"
                          className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                          id="fullName"
                          name="fullName"
                          placeholder="Enter full name"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          maxLength="100"
                        />
                        {errors.fullName && (
                          <div className="invalid-feedback d-block">{errors.fullName}</div>
                        )}
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="mb-4">
                      <label htmlFor="emailID" className="form-label fw-bold">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaEnvelope />
                        </span>
                        <input
                          type="email"
                          className={`form-control ${errors.emailID ? 'is-invalid' : ''}`}
                          id="emailID"
                          name="emailID"
                          placeholder="Enter email address"
                          value={formData.emailID}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        {errors.emailID && (
                          <div className="invalid-feedback d-block">{errors.emailID}</div>
                        )}
                      </div>
                    </div>

                    {/* Mobile Number Field */}
                    <div className="mb-4">
                      <label htmlFor="mobileNumber" className="form-label fw-bold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaPhone />
                        </span>
                        <input
                          type="tel"
                          className={`form-control ${errors.mobileNumber ? 'is-invalid' : ''}`}
                          id="mobileNumber"
                          name="mobileNumber"
                          placeholder="Enter 10-digit mobile number"
                          value={formData.mobileNumber}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          maxLength="15"
                        />
                        {errors.mobileNumber && (
                          <div className="invalid-feedback d-block">{errors.mobileNumber}</div>
                        )}
                      </div>
                    </div>

                    {/* Role Field */}
                    <div className="mb-4">
                      <label htmlFor="role" className="form-label fw-bold">
                        Role <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaUser />
                        </span>
                        <select
                          className="form-select form-control-display-only"
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          disabled
                        >
                          <option value="Admin">Admin</option>
                        </select>
                      </div>
                      <small className="text-muted">Role is set to Admin</small>
                    </div>

                    {/* Password Field */}
                    <div className="mb-4">
                      <label htmlFor="password" className="form-label fw-bold">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                          id="password"
                          name="password"
                          placeholder="Enter password (min. 6 characters)"
                          value={formData.password}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        {errors.password && (
                          <div className="invalid-feedback d-block">{errors.password}</div>
                        )}
                      </div>
                      <small className="text-muted">Minimum 6 characters required</small>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="mb-4">
                      <label htmlFor="confirmPassword" className="form-label fw-bold">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Re-enter password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        {errors.confirmPassword && (
                          <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
                        )}
                      </div>
                    </div>

                    {/* Active Status */}
                    <div className="mb-4">
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="isActive"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <label className="form-check-label" htmlFor="isActive">
                          Active User
                        </label>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Registering...' : 'Register Admin User'}
                      </button>
                    </div>
                  </form>

                  {/* Back Link */}
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => navigate('/admin/dashboard')}
                      disabled={isLoading}
                    >
                      Back to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminUserRegister;
